import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  QuestionAnswer as QuestionAnswerIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Image as ImageIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { auth, db } from '../firebase/config'
import { collection, query, where, getDocs, doc, getDoc, orderBy } from 'firebase/firestore'
import toast from 'react-hot-toast'

const StudentAnswers = ({ onBack }) => {
  const { user } = useAuth()
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user && user.uid) {
      loadQuestions()
    }
  }, [user])

  const loadQuestions = async () => {
    try {
      setLoading(true)
      console.log('🔍 Gelen yanıtlar yükleniyor...')
      console.log('👤 Kullanıcı ID:', user.uid)
      
      // Öğrencinin sorularını al
      const questionsRef = collection(db, 'studentQuestions')
      const questionsQuery = query(
        questionsRef,
        where('studentId', '==', user.uid)
      )
      
      const questionsSnapshot = await getDocs(questionsQuery)
      console.log('📝 Bulunan soru sayısı:', questionsSnapshot.size)
      
      // Tüm soruları debug için göster
      questionsSnapshot.docs.forEach((doc, index) => {
        console.log(`📄 Soru ${index + 1}:`, doc.data())
      })
      
      const questionsData = []
      
      for (const questionDoc of questionsSnapshot.docs) {
        const questionData = questionDoc.data()
        console.log('🔍 Soru verisi:', questionData)
        
        // Öğretmen adını al
        let teacherName = 'Bilinmeyen Öğretmen'
        if (questionData.teacherId) {
          try {
            const teacherDoc = await getDoc(doc(db, 'users', questionData.teacherId))
            if (teacherDoc.exists()) {
              teacherName = teacherDoc.data().username || teacherDoc.data().email || 'Bilinmeyen Öğretmen'
            }
          } catch (error) {
            console.error('Error loading teacher name:', error)
          }
        }
        
        questionsData.push({
          id: questionDoc.id,
          teacherName,
          description: questionData.description || '',
          imageUrl: questionData.imageUrl || '',
          status: questionData.status || 'pending',
          teacherResponse: questionData.teacherResponse || '',
          teacherVoiceResponse: questionData.teacherVoiceResponse || '',
          sentDate: questionData.sentDate?.toDate() || new Date()
        })
      }
      
      console.log('📋 Yüklenen sorular:', questionsData)
      
      // Manuel sıralama (en yeni üstte)
      questionsData.sort((a, b) => b.sentDate - a.sentDate)
      
      setQuestions(questionsData)
      
    } catch (error) {
      console.error('Error loading questions:', error)
      toast.error('Sorular yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning'
      case 'answered':
        return 'success'
      case 'declined':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Bekliyor'
      case 'answered':
        return 'Cevaplandı'
      case 'declined':
        return 'Reddedildi'
      default:
        return 'Bilinmiyor'
    }
  }

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const handleQuestionClick = (question) => {
    // Detay sayfasına yönlendir
    onBack({ 
      page: 'answer-detail', 
      questionData: question 
    })
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Gelen Yanıtlar
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {!user || !user.uid ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Kullanıcı bilgileri yükleniyor...</Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Sorular yükleniyor...</Typography>
          </Box>
        ) : questions.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <QuestionAnswerIcon sx={{ fontSize: 60, color: '#6E6D6D', mb: 2 }} />
              <Typography variant="h6" color="#6E6D6D" gutterBottom>
                Henüz soru göndermediniz
              </Typography>
              <Typography variant="body2" color="#6E6D6D">
                Öğretmenlerinize soru gönderdiğinizde yanıtlar burada görünecek
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <List>
            {questions.map((question, index) => (
              <Card
                key={question.id}
                sx={{
                  mb: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: 3
                  }
                }}
                onClick={() => handleQuestionClick(question)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Soru Görseli */}
                    {question.imageUrl && (
                      <Avatar
                        variant="rounded"
                        sx={{ width: 60, height: 60, flexShrink: 0 }}
                      >
                        <img
                          src={question.imageUrl}
                          alt="Soru görseli"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </Avatar>
                    )}
                    
                    {/* Soru İçeriği */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip
                          label={getStatusText(question.status)}
                          color={getStatusColor(question.status)}
                          size="small"
                          icon={
                            question.status === 'answered' ? <CheckCircleIcon /> :
                            question.status === 'declined' ? <CancelIcon /> :
                            <ScheduleIcon />
                          }
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(question.sentDate)}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Öğretmen:</strong> {question.teacherName}
                      </Typography>
                      
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'medium',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {question.description}
                      </Typography>
                      
                      {/* Öğretmen yanıtı varsa göster */}
                      {question.status === 'answered' && question.teacherResponse && (
                        <Box sx={{ mt: 1, p: 1, backgroundColor: 'success.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="success.main" fontWeight="bold">
                            Öğretmen Yanıtı:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {question.teacherResponse}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Sesli yanıt varsa göster */}
                      {question.status === 'answered' && question.teacherVoiceResponse && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label="Sesli Yanıt"
                            size="small"
                            color="info"
                            icon={<QuestionAnswerIcon />}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </List>
        )}
      </Container>
      </Box>
    </SpaceTheme>
  )
}

export default StudentAnswers
